<template>
  <div class="materials-tab">
    <dx-data-grid
      @toolbar-preparing="onToolbarPreparing($event)"
      :columnChooser="{
        enabled: false,
      }"
      :pager="{ visible: false }"
      :columns="getWorkOrderMaterialColumns"
      :data-source="getWorkOrderMaterialDataSource"
      :row-alternation-enabled="true"
      :selection="{
        mode: 'none',
      }"
      :height="height"
      :ref="'materials-tab-grid'"
      :element-attr="{
        class: 'materials-tab-grid',
      }"
      @editing-start="onEditingStart"
      :focusedRowEnabled="popupMode === 'edit'"
      keyExpr="id"
      @editor-preparing="onEditorPreparing"
      @row-removed="onRowRemoved"
      @row-prepared="onRowPrepared"
    >
      <DxEditing
        :allow-updating="popupMode != 'review'"
        :mode="'cell'"
        :changes="changes"
        :confirm-delete="false"
        :allow-deleting="false"
      />
      <template #Record="{ data }">
        <GcRecord
          :data="data"
          :primary="data.column['cellTemplateRecord']['primary']"
          :secondary="data.column['cellTemplateRecord']['secondary']"
        />
      </template>

      <template #Decimal="{ data }">
        <GcDecimal :dataValue="data" :format="data.column.format" />
      </template>
    </dx-data-grid>
  </div>
</template>

<script>
import ArrayStore from "devextreme/data/array_store";
import ColumnWidth from "../../../_helpers/data-grid/ColumnWidth";
import dataGridMixin from "../../../_mixins/data-grid.mixin";
import { WorkOrderService } from "../../../_services/api/production/work-order.service";
import DataGridHelpers from "@/_helpers/data-grid/helpers";
import { backwardTabbing, forwardTabbing } from "../../../_helpers/ui/keyboard";
const { countLineBreaks } = DataGridHelpers;

const baseLine = {
  ProcessCode: null,
  ProductCode: null,
  Description: null,
  StockUM: null,
  WorkOrderQty: 0,
  ActualQty: 0,
  OutstandingQty: 0,
  WorkOrderCost: 0,
  ActualCost: 0,
  OutstandingCost: 0,
  Comment: "",
  Completed: "N",
  IsEditable: true,
  RecordId: "00000000-0000-0000-0000-000000000000",
  ExTaxUnitCost: 0,
};

export default {
  mixins: [dataGridMixin],
  components: {},
  props: {
    dataModel: {
      type: Object,
      default: () => ({}),
    },
    height: {
      type: [Number, String],
      default: 280,
    },
    displayServerError: {
      type: Function,
      default: function () {},
    },
    showLoading: {
      type: Function,
      default: () => {},
    },
    popupMode: {
      type: String,
      default: "create",
    },
    refreshOnUpdate: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      WorkOrderInstance: "Core",
      changes: [],
      workOrderMaterialDataSource: new ArrayStore({
        key: "id",
        data: [],
      }),
    };
  },
  async created() {
    await this.getWorkOrderMaterialList(this.dataModel.WorkOrderCode);

    this.$nextTick(() => {
      if (this.popupMode === "edit") {
        this.addNewRowToGrid();
      }
    });
  },
  methods: {
    addNewRowToGrid(refresh = false) {
      this.workOrderMaterialDataSource.insert({
        ...baseLine,
        WorkOrderCode: this.dataModel.WorkOrderCode,
      });

      if (refresh) {
        setTimeout(() => {
          this.dataGrid.saveEditData();
          this.dataGrid.refresh(true);

          this.$nextTick(() => {
            this.dataGrid.updateDimensions();
          });
        }, 100);
      }
    },
    async getWorkOrderMaterialList(code) {
      if (this.popupMode !== "edit" || this.refreshOnUpdate) {
        this.workOrderMaterialDataSource.clear();
      }

      await this.$nextTick();

      const res = await WorkOrderService.getWorkOrderMaterialList(code);
      if (!res) return;

      const records = res.data.value || [];
      let newLines = [];

      if (this.popupMode !== "edit" || this.refreshOnUpdate) {
        // Insert all records in view-only mode
        records.forEach((element) => {
          this.workOrderMaterialDataSource.insert({
            ...element,
            IsEditable: false,
            id: element.RecordId,
          });
        });
        return;
      }

      // Collect only new records in edit mode
      const currentRecords = this.workOrderMaterialDataSource._array;
      records.forEach((element) => {
        if (!currentRecords.some((item) => item.id === element.RecordId)) {
          newLines.push({
            ...element,
            IsEditable: true,
            id: element.RecordId,
          });
        }
      });

      if (newLines.length) {
        const emptyLine = currentRecords.find((line) => !line.ProcessCode);
        if (emptyLine) {
          this.workOrderMaterialDataSource.remove(emptyLine.id);
        }

        newLines.forEach((line) =>
          this.workOrderMaterialDataSource.insert(line)
        );

        if (emptyLine) {
          delete emptyLine.id;
          this.workOrderMaterialDataSource.insert(emptyLine);
        }

        await this.$nextTick();
        this.dataGrid.saveEditData();
        this.dataGrid.repaint();

        setTimeout(() => {
          this.$nextTick(() => this.dataGrid.updateDimensions());
        }, 1000);
      }
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.visible = false;
    },

    onEditingStart(e) {
      const rowData = e.data;
      if (rowData && this.popupMode === "edit") {
        if (e.column.dataField) {
          if (
            rowData.IsEditable === false &&
            (e.column.dataField === "ProcessCode" ||
              e.column.dataField === "ProductCode")
          ) {
            e.cancel = true;
          }

          if (
            e.column.dataField === "Completed" &&
            rowData.OutstandingQty === 0
          ) {
            e.cancel = true;
          }
        }

        if (e.dataField && e.dataField == "ProcessCode") {
          e.editorName = "dxSelectBox";
        }
      }
    },
    commentFieldHeightUpdate(e) {
      let instance = e.component;
      let count = countLineBreaks(instance.instance().option().value);
      // Adjust the height of the comment field based on the number of line breaks
      // If there are less than 7 line breaks, set the height to count * 16
      // If there are 7 or more line breaks, set the height to 250px
      // This is to ensure that the comment field does not grow too large
      // and remains user-friendly.
      // The height is set to 16px per line break, with a maximum height of 250px
      if (count < 7) {
        if (count > 2) {
          instance.instance().beginUpdate();
          instance.instance().option("height", count * 16);
          instance.instance().endUpdate();
          this.dataGrid.updateDimensions();
        }
      } else {
        instance.instance().beginUpdate();
        instance.instance().option("height", 250);
        instance.instance().endUpdate();
        this.dataGrid.updateDimensions();
      }
    },
    onEditorPreparing(e) {
      if (e.dataField && e.dataField == "ProcessCode") {
        e.editorName = "dxSelectBox";
      }
      if (e.dataField && e.dataField == "Comment") {
        e.editorName = "dxTextArea";
      }

      if (e.parentType !== "searchPanel") {
        const { dataField, editorOptions, row } = e;
        // Only wrap handlers if not in the search panel
        if (!editorOptions) return;

        const wrapHandler = (handlerName) => {
          const original = editorOptions[handlerName];
          if (typeof original === "function") {
            editorOptions[handlerName] = (event) => {
              original(event, {
                column: { dataField },
                data: row.data,
                rowIndex: row.rowIndex,
              });
            };
          }
        };

        // Wrap each handler if it exists
        [
          "onValueChanged",
          "onItemClick",
          "onKeyDown",
          "onOpened",
          "onFocusIn",
        ].forEach(wrapHandler);
      }
    },
    getProductCodeSource(items) {
      let filter = [["Inactive", "=", "N"]];
      let productFilter = [];

      items.forEach((item) => {
        if (item.ProductCode) {
          if (productFilter.length > 0) {
            productFilter.push("and");
          }
          productFilter.push(["Code", "<>", item.ProductCode]);
        }
      });

      if (productFilter.length > 0) {
        filter.push("and");
        filter.push(productFilter);
      }

      return WorkOrderService.dataSourceConfig("api/Product", {
        filter: filter,
        sort: "Code asc",
        select:
          "Code,Description1,Inactive, RecordId, UnitOfMeasureCode, StandardCost",
        paginate: true,
      });
    },
    onProductCodeValueChanged(e, lineData) {
      if (!e.event) return;
      if (e.value) {
        let productRecordId = e.value;
        const component = e.component; // the dxSelectBox instance
        const productData = component
          .option("items")
          .find((item) => item.RecordId === productRecordId);

        if (productData) {
          this.workOrderMaterialDataSource.update(lineData.data.id, {
            ProductCode: productData.Code,
            Description: productData.Description1,
            StockUM: productData.UnitOfMeasureCode,
            ExTaxUnitCost: productData.StandardCost,
          });

          this.dataGrid.editCell(
            lineData.rowIndex,
            "ProductCode",
            productData.Code
          );
        }
      }
    },

    async onRowRemoved(e) {
      const dataField = "ProcessCode";
      const items = await this.workOrderMaterialDataSource.load();
      if (items.length === 0) {
        this.workOrderMaterialDataSource.insert(baseLine);
        e.component.refresh();
        await this.$nextTick();

        setTimeout(() => {
          this.focusOnEditableCell(0, dataField);
        }, 100);
      } else if (e.data && !e.data.ProcessCode) {
        this.workOrderMaterialDataSource.insert(baseLine).then((line) => {
          e.component.refresh();
          this.$nextTick(() => {
            setTimeout(() => {
              const rowIndex = e.component.getRowIndexByKey(line.id);
              if (rowIndex !== -1) {
                this.focusOnEditableCell(rowIndex, dataField);
              }
              this.focusOnEditableCell(rowIndex, dataField);
            }, 100);
          });
        });
      } else {
        if (e.data && (!e.data.ProcessCode || e.data.ProcessCode === "")) {
          e.component.refresh();
          await this.$nextTick();

          setTimeout(() => {
            this.focusOnEditableCell(items.length, dataField);
          }, 100);
        }
      }
    },
    focusOnEditableCell(rowIndex, col) {
      const column = col ? col : "ProcessCode";
      const dataGrid = this.dataGrid;
      const cellElement = dataGrid.getCellElement(rowIndex, column);
      dataGrid.focus(cellElement);
      setTimeout(() => {
        dataGrid.editCell(rowIndex, column);
      }, 10);
    },

    async getMaterialLines() {
      const items = await this.workOrderMaterialDataSource.load({
        filter: [["ProcessCode", "<>", null], "and", ["ProcessCode", "<>", ""]],
      });

      return items.map((item) => {
        return {
          ProcessCode: item.ProcessCode,
          ProductCode: item.ProductCode,
          StockUM: item.StockUM,
          WorkOrderQty: item.WorkOrderQty,
          ActualQty: item.ActualQty,
          OutstandingQty: item.OutstandingQty,
          Completed: item.Completed,
          Comment: item.Comment,
          OutstandingCost: item.OutstandingCost,
          // IsPrimeCostItem: item.IsPrimeCostItem,
          ExTaxUnitCost: item.ExTaxUnitCost,
          StandardUnitCost: item.StandardUnitCost,
          WorkOrderCode: item.WorkOrderCode,
          IssueType: item.IssueType || "Material",
          Status: item.Status || "A",
          MaterialCode: item.ProductCode,
          RecordId: item.RecordId || "00000000-0000-0000-0000-000000000000",
        };
      });
    },
    setFocusOnHeaderForm() {
      this.$emit("setFocusOnField", "Comments", false, true);
    },
    handleTabNavigation(e, lineData, checkOutstandingQty = false) {
      if (forwardTabbing(e)) {
        if (!checkOutstandingQty || lineData.data.OutstandingQty === 0) {
          const isLastRow =
            lineData.rowIndex ===
            this.workOrderMaterialDataSource._array.length - 1;

          if (isLastRow) {
            e.event.preventDefault();
            return;
          }

          const nextRowID = this.dataGrid.getKeyByRowIndex(
            lineData.rowIndex + 1
          );

          if (nextRowID) {
            this.dataGrid.byKey(nextRowID).done((nextRowData) => {
              e.event.preventDefault();
              const nextField =
                nextRowData && nextRowData.IsEditable
                  ? "ProcessCode"
                  : "WorkOrderQty";

              this.focusOnEditableCell(lineData.rowIndex + 1, nextField);
            });
          }
        }
      }
    },

    onCommentKeyDown(e, lineData) {
      this.handleTabNavigation(e, lineData, true);
    },

    onCompletedKeyDown(e, lineData) {
      this.handleTabNavigation(e, lineData, false);
    },
    onProcessCodeBackwardTabbing(e, lineData) {
      const prevRowID = this.dataGrid.getKeyByRowIndex(lineData.rowIndex - 1);
      if (prevRowID) {
        this.dataGrid.byKey(prevRowID).done((prevRowData) => {
          e.event.preventDefault();

          const nextField =
            prevRowData && prevRowData.OutstandingQty === 0
              ? "Comment"
              : "Completed";

          this.focusOnEditableCell(lineData.rowIndex - 1, nextField);
        });
      }
    },
    onWorkOrderQtyBackwardTabbing(e, lineData) {
      this.onProcessCodeBackwardTabbing(e, lineData);
    },
    async updateDeletedMaterialLines(deletedLines) {
      const emptyLine = this.workOrderMaterialDataSource._array.find(
        (line) => !line.ProcessCode
      );

      if (emptyLine) {
        this.workOrderMaterialDataSource.remove(emptyLine.id);
      }

      deletedLines.forEach((line) => {
        line.IsDeleted = true;
        this.workOrderMaterialDataSource.insert(line);
      });

      if (emptyLine) {
        delete emptyLine.id;
        this.workOrderMaterialDataSource.insert(emptyLine);
      }

      await this.$nextTick();
      this.dataGrid.saveEditData();
      this.dataGrid.repaint();
      setTimeout(() => {
        this.$nextTick(() => {
          this.dataGrid.updateDimensions();
        });
      }, 1000);
    },

    onRowPrepared(e) {
      if (e.rowType === "data") {
        if (e.data.IsDeleted && this.popupMode === "edit") {
          e.rowElement.classList.add("alert-row");
          if (e.rowElement.classList.contains("dx-row-alt")) {
            e.rowElement.classList.remove("dx-row-alt");
          }
        }
      }
    },
  },
  computed: {
    dataGrid() {
      return this.$refs["materials-tab-grid"].instance;
    },
    getWorkOrderMaterialDataSource() {
      return this.workOrderMaterialDataSource._array;
    },
    getWorkOrderMaterialColumns() {
      return [
        {
          caption: "Process",
          dataField: "ProcessCode",
          width: ColumnWidth.ProcessCode.width,
          minWidth: ColumnWidth.ProcessCode.minWidth,
          alignment: "left",
          allowSorting: true,
          allowEditing: true,
          editorType: "dxSelectBox",
          cssClass: "edit-cell-textbox-field",
          editorOptions: {
            dataSource: WorkOrderService.dataSourceConfig(
              "api/WorkOrderLabour",
              {
                filter: [
                  ["Inactive", "=", "N"],
                  ["WorkOrderCode", "=", this.dataModel.WorkOrderCode],
                ],
                sort: "ProcessSequence asc",
                select:
                  "ProcessCode,Inactive, WorkOrderCode, ProcessSequence, RecordId",
              }
            ),
            displayExpr: "ProcessCode",
            valueExpr: "ProcessCode",
            searchEnabled: true,
            searchExpr: ["ProcessCode"],
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              if (backwardTabbing(e) && lineData.rowIndex === 0) {
                e.event.preventDefault();
                this.setFocusOnHeaderForm();
              } else if (backwardTabbing(e)) {
                this.onProcessCodeBackwardTabbing(e, lineData);
              }
            },
            onItemClick: (e, lineData) => {
              if (e.event)
                this.focusOnEditableCell(lineData.rowIndex, "ProductCode");
            },
          },
          setCellValue: async (newData, value, currentRowData) => {
            if (!currentRowData.ProcessCode) {
              this.addNewRowToGrid(true);
            }
            newData.ProcessCode = value;
            return newData;
          },
        },
        {
          caption: "Product",
          dataField: "ProductCode",
          width: ColumnWidth.Code.width,
          minWidth: ColumnWidth.Code.minWidth,
          alignment: "left",
          allowSorting: true,
          allowEditing: true,
          cssClass: "edit-cell-textbox-field",
          editorType: "dxSelectBox",
          editorOptions: {
            displayExpr: "Code",
            valueExpr: "RecordId",
            searchTimeout: 10,
            searchEnabled: true,
            searchExpr: ["Code", "Description1"],
            onFocusIn: async (e, lineData) => {
              const items = await this.workOrderMaterialDataSource.load({
                filter: [
                  ["id", "<>", lineData.data.id],
                  "and",
                  ["ProcessCode", "=", lineData.data.ProcessCode],
                ],
              });
              const selectBox = e.component;
              selectBox.option("dataSource", this.getProductCodeSource(items));
              await this.$nextTick();
              setTimeout(() => {
                selectBox.focus();
                this.$nextTick(() => {
                  e.event.target.select();
                });
              }, 200);
            },
            itemTemplate: (data) => {
              return ` <div class="custom-auto-complete" id="${data.Code}" >
                <span class="auto-complete-primary">
                  ${data.Code}
                </span>
                <span class="record-secondary gc-grid-code">
                  ${data.Description1}
                </span>
              </div>`;
            },
            dropDownOptions: {
              minWidth: 400,
            },
            onValueChanged: (e, lineData) => {
              this.onProductCodeValueChanged(e, lineData);
            },
            onItemClick: (e, lineData) => {
              if (e.event)
                this.focusOnEditableCell(lineData.rowIndex, "WorkOrderQty");
            },
          },
          setCellValue: async (newData, value) => {
            newData.ProductCode = value;
            return newData;
          },
        },
        {
          caption: "Description",
          dataField: "Description",
          minWidth: ColumnWidth.Description.minWidth,
          alignment: "left",
          allowSorting: true,
          allowEditing: false,
        },
        {
          caption: "Stock UM",
          dataField: "StockUM",
          alignment: "left",
          width: ColumnWidth.DeliveryNumber.width,
          minWidth: ColumnWidth.DeliveryNumber.minWidth,
          allowSorting: true,
          allowEditing: false,
        },
        {
          caption: "Plan Qty",
          dataField: "WorkOrderQty",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          allowSorting: true,
          format: "##,###,###,###,##0.0000",
          allowEditing: true,
          cssClass: "edit-cell-numberbox-field",
          editorOptions: {
            format: "##,###,###,###,##0.0000",
            onFocusIn: (e) => {
              e.event.target.select();
            },
            onKeyDown: (e, lineData) => {
              if (
                backwardTabbing(e) &&
                !lineData.data.IsEditable &&
                lineData.rowIndex === 0
              ) {
                e.event.preventDefault();
                this.setFocusOnHeaderForm();
              } else if (backwardTabbing(e) && !lineData.data.IsEditable) {
                this.onWorkOrderQtyBackwardTabbing(e, lineData);
              }
            },
          },
          // setCellValue: async (newData, value, currentRowData) => {
          setCellValue: async (newData, value, currentRowData) => {
            /*
            if (value < currentRowData.ActualQty) {
              await this.$alert(
                "The new Planned Qty must be greater than or equal to the current Actual Qty.",
                "Validation Error"
              );
              newData.WorkOrderQty = currentRowData.WorkOrderQty;

              this.workOrderMaterialDataSource.update(
                currentRowData.id,
                newData
              );

              this.dataGrid.saveEditData();
              this.dataGrid.refresh(true);

              this.$nextTick(() => {
                const rowIndex = this.dataGrid.getRowIndexByKey(
                  currentRowData.id
                );
                if (rowIndex >= 0) {
                  this.dataGrid.editCell(rowIndex, "WorkOrderQty");
                }
              });
            } else {
              */
            newData.WorkOrderQty = value;

            newData.OutstandingQty = value - currentRowData.ActualQty;
            newData.WorkOrderCost = (
              value * currentRowData.ExTaxUnitCost
            ).roundNumber(4);
            newData.OutstandingCost =
              newData.WorkOrderCost - currentRowData.ActualCost;
            // }
            return newData;
          },
        },
        {
          caption: "Act Qty",
          dataField: "ActualQty",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          allowSorting: true,
          format: "##,###,###,###,##0.0000",
          allowEditing: false,
        },
        {
          caption: "O/S Qty",
          dataField: "OutstandingQty",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          allowSorting: true,
          cssClass: "os-qty",
          format: "##,###,###,###,##0.0000",
          allowEditing: false,
        },
        {
          caption: "Plan Cost",
          dataField: "WorkOrderCost",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          allowSorting: true,
          cssClass: "os-qty",
          format: "##,###,###,###,##0.0000",
          allowEditing: false,
        },
        {
          caption: "Act Cost",
          dataField: "ActualCost",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          allowSorting: true,
          cssClass: "os-qty",
          format: "##,###,###,###,##0.0000",
          allowEditing: false,
        },
        {
          caption: "O/S Cost",
          dataField: "OutstandingCost",
          width: ColumnWidth.Quantity4DecimalPoints.width,
          minWidth: ColumnWidth.Quantity4DecimalPoints.minWidth,
          allowSorting: true,
          cssClass: "os-qty",
          format: "##,###,###,###,##0.0000",
          allowEditing: false,
        },
        {
          caption: "Comment",
          dataField: "Comment",
          minWidth: ColumnWidth.Comment.minWidth,
          cssClass: "edit-cell-textbox-field comment-text",
          editorType: "dxTextArea",
          customizeText: (e) => {
            if (e.value) return e.value.replaceAll("\n", "<br/>\r\n");
            else return "";
          },
          encodeHtml: false,
          cellTemplate: (data, info) => {
            return (data.innerHTML = `<div class='comment-cell-template'>${info.text}</div>`);
          },
          allowEditing: true,
          editorOptions: {
            onFocusIn: (e) => {
              e.event.target.select();
            },
            maxLength: 100,
            maxHeight: 103,
            // height: 30,
            valueChangeEvent: "input",
            onInput: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onInitialized: (e) => {
              this.commentFieldHeightUpdate(e);
            },
            onKeyDown: (e, lineData) => {
              this.onCommentKeyDown(e, lineData);
            },
          },
          setCellValue: async (newData, value) => {
            newData.Comment = value;
            return newData;
          },
        },
        {
          caption: "Completed",
          dataField: "Completed",
          editorType: "dxCheckBox",
          width: 70,
          dataType: "boolean",
          calculateCellValue: (data) => {
            return data.Completed === "Y" ? true : false;
          },
          editorOptions: {
            onKeyDown: (e, lineData) => {
              this.onCompletedKeyDown({ event: e }, lineData);
            },
          },
          allowEditing: true,
          setCellValue: async (newData, value) => {
            newData.Completed = value ? "Y" : "N";
            return newData;
          },
        },
        {
          dataField: "",
          caption: "",
          type: "buttons",
          buttons: [
            {
              hint: "Delete",
              icon: "trash",

              visible: (e) => {
                const data = e.row.data;
                if (
                  data &&
                  data.IsDeletable === "Y" &&
                  this.popupMode === "edit"
                ) {
                  return true;
                } else {
                  return false;
                }
              },
              onClick: async (e) => {
                const confirmDelete = await this.$confirm(
                  "Are you sure you want to delete this material? This action cannot be undone unless re-added manually.",
                  "Delete Entry"
                );
                if (confirmDelete) {
                  this.dataGrid.deleteRow(e.row.rowIndex);
                }
              },
            },
          ],
          visible: this.popupMode === "edit",
        },
      ];
    },
  },
  watch: {
    popupMode(newValue) {
      if (newValue === "edit") {
        this.addNewRowToGrid();
      }
    },
    refreshOnUpdate(newValue) {
      if (newValue) {
        this.getWorkOrderMaterialList(this.dataModel.WorkOrderCode);
      }
    },
  },
};
</script>

<style lang="scss">
.materials-tab {
  .alert-row {
    background-color: #ffcccc; /* Light red background */
  }
}
</style>
